// Centralized static audio and text configuration for all tasks
export type StaticAudioType = 'start' | 'end' | 'intro' | 'start_prep' | 'start_exam' | 'task_completed' | 'trail-topic';

export interface StaticAudioConfig {
  type: StaticAudioType;
  text: string;
  url: string;
  task: 'task1' | 'task2' | 'task3' | 'common';
}

// Base URL for static audio files
const STATIC_AUDIO_BASE_URL = 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio';

// All static audio configurations
export const STATIC_AUDIO_CONFIGS: StaticAudioConfig[] = [
  // Task 1 Audio
  {
    type: 'start',
    text: "Tâche 1, Parlez-moi de vous pendant environ 2 minutes.",
    url: `${STATIC_AUDIO_BASE_URL}/task1-start-fr.mp3`,
    task: 'task1'
  },
  {
    type: 'end',
    text: "C'est la fin de la tâche 1.",
    url: `${STATIC_AUDIO_BASE_URL}/task1-end-fr.mp3`,
    task: 'task1'
  },

  // Task 2 Audio
  {
    type: 'intro',
    text: "Tâche 2. Nous allons maintenant faire un jeu de rôle. Votre tâche consiste à me poser des questions.",
    url: `${STATIC_AUDIO_BASE_URL}/task2-intro-fr.mp3`,
    task: 'task2'
  },
  {
    type: 'start_prep',
    text: "Vous avez 2 minutes pour vous préparer.",
    url: `${STATIC_AUDIO_BASE_URL}/task2-start-prep.mp3`,
    task: 'task2'
  },
  {
    type: 'start_exam',
    text: "Vous pouvez commencer quand vous le souhaitez.",
    url: `${STATIC_AUDIO_BASE_URL}/task2-start-exam.mp3`,
    task: 'task2'
  },
  {
    type: 'end',
    text: "C'est la fin de la tâche 2.",
    url: `${STATIC_AUDIO_BASE_URL}/task2-end.mp3`,
    task: 'task2'
  },
  {
    type: 'trail-topic',
    text: "Je fais partie d’un groupe qui soutient les personnes âgées dans notre quartier. Vous êtes intéressé et vous me posez des questions (activités, bénévoles, horaires, organisation).",
    url: `${STATIC_AUDIO_BASE_URL}/task2-trial-audio.mp3`,
    task: 'task2'
  },  

  // Task 3 Audio
  {
    type: 'start',
    text: "Tâche 3, Parlez-moi de votre projet professionnel pendant environ 2 minutes.",
    url: `${STATIC_AUDIO_BASE_URL}/task3-start-fr.mp3`,
    task: 'task3'
  },
  {
    type: 'end',
    text: "C'est la fin de la tâche 3.",
    url: `${STATIC_AUDIO_BASE_URL}/task3-end-fr.mp3`,
    task: 'task3'
  },
  {
    type: 'task_completed',
    text: "Félicitations ! Vous avez terminé toutes les tâches de l'examen.",
    url: `${STATIC_AUDIO_BASE_URL}/task-completed-fr.mp3`,
    task: 'task3'
  },
  {
    type: 'trail-topic',
    text: "Certains pensent que les réseaux sociaux rapprochent les gens, d’autres qu’ils les isolent. Quelle est votre opinion ?",
    url: `${STATIC_AUDIO_BASE_URL}/task3-trail-audio.mp3`,
    task: 'task3'
  },

  // Common Audio (if any)
  // Add common audio files here if needed
];

// Helper functions to get static audio by type and task
export const getStaticAudioByType = (type: StaticAudioType, task?: 'task1' | 'task2' | 'task3' | 'common'): StaticAudioConfig | undefined => {
  if (task) {
    return STATIC_AUDIO_CONFIGS.find(config => config.type === type && config.task === task);
  }
  return STATIC_AUDIO_CONFIGS.find(config => config.type === type);
};

export const getStaticAudioByTask = (task: 'task1' | 'task2' | 'task3' | 'common'): StaticAudioConfig[] => {
  return STATIC_AUDIO_CONFIGS.filter(config => config.task === task);
};

// Helper function to get all types for a specific task
export const getAvailableTypesForTask = (task: 'task1' | 'task2' | 'task3' | 'common'): StaticAudioType[] => {
  const configs = getStaticAudioByTask(task);
  return configs.map(config => config.type);
};

// Helper function to validate if a type exists for a task
export const hasStaticAudio = (type: StaticAudioType, task: 'task1' | 'task2' | 'task3' | 'common'): boolean => {
  return getStaticAudioByType(type, task) !== undefined;
};

// Export individual task configurations for backward compatibility
export const TASK1_STATIC_AUDIO = getStaticAudioByTask('task1');
export const TASK2_STATIC_AUDIO = getStaticAudioByTask('task2');
export const TASK3_STATIC_AUDIO = getStaticAudioByTask('task3');